import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import FormButton from '@components/form/form-button';
import { Modal } from '@components/modal';

import companyIcon from '../../assets/companyIcon.svg';
import warningIcon from '../../assets/warningIcon.svg';
import { useVismaConfiguration } from '../../services/visitor-management';
import type { Registrant } from '../../types';

const Header = styled.div`
  gap: 16px;
  display: flex;
  align-items: flex-start;
`;

const ModalText = styled.p`
  margin: 12px 0;
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};
  & > b {
    color: ${({ theme }) => theme.primary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const BoldText = styled.h4`
  font-weight: 600;
  font-size: 20px;
  color: ${({ theme }) => theme.primary};
`;

const RegistrantContainer = styled.div`
  padding: 10px 16px;
  background: ${({ theme }) => theme.white};
  box-shadow:
    0 0 4px rgba(0, 0, 0, 0.04),
    0 4px 8px rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  margin-bottom: 16px;
  & > h4 {
    font-weight: 600;
    font-size: 18px;
    color: ${({ theme }) => theme.text};
  }
  & > div {
    display: flex;
    & > img {
      margin-right: 8px;
    }
    & > span {
      font-weight: 400;
      font-size: 16px;
      color: ${({ theme }) => theme.textSecondary};
    }
  }
`;

interface VideoConfirmWarningProps {
  open: boolean;
  closeModal: () => void;
  registrants: Registrant[];
  onConfirm: () => void;
}

function VideoConfirmWarning({
  open,
  closeModal,
  registrants,
  onConfirm
}: VideoConfirmWarningProps) {
  const intl = useIntl();
  const { configuration } = useVismaConfiguration();
  const multi = configuration?.elearning?.multi;
  const dataField = configuration?.personalData?.data_field;
  return (
    <Modal open={open} onCancel={closeModal} footer={null}>
      <Header>
        <img src={warningIcon} alt="warning" style={{ marginTop: 10 }} />
        <BoldText>{multi?.wait_for_video}</BoldText>
      </Header>

      <ModalText
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage({ id: 'VIDEO_CONFIRM_MODAL_DESC' })
        }}
      />

      {registrants.map((registrant, index) => (
        <RegistrantContainer key={index}>
          <h4>{registrant.fullname}</h4>
          {registrant.companyName && !dataField?.company_name?.disabled && (
            <div>
              <img src={companyIcon} alt="company" />
              <span>{registrant.companyName}</span>
            </div>
          )}
        </RegistrantContainer>
      ))}

      <ButtonContainer>
        <FormButton withLeftArrow variant="outlined" onClick={closeModal}>
          {intl.formatMessage({ id: 'CANCEL' })}
        </FormButton>
        <FormButton
          withRightArrow
          type="primary"
          onClick={onConfirm}
          style={{ marginLeft: 16 }}
        >
          {intl.formatMessage({ id: 'ACCEPT_AND_FINISH' })}
        </FormButton>
      </ButtonContainer>
    </Modal>
  );
}

export default VideoConfirmWarning;
